import React, { useCallback, useState, useEffect, useRef } from "react";
import { ItemBaseProps, ItemContainerWrapper } from "../itemContainer";
import { ItemRenderData } from "@app/types/itemRenderData";
import { MediaSliderV2 } from "@app/components/questkit/MediaSliderV2";
import { useQuestViewContext } from "@app/quest/QuestViewContext";
import { MediaSliderState } from "@app/components/questkit/MediaSliderV2/MediaSliderState";
import { apiRequest } from "@app/util/client";
import { QuestInstances } from "@questmate/openapi-spec";
import {
  MinimalFile,
  getPersistableFileFields,
  PersistableFile,
  PersistedFileFromServer,
  FileUpload,
} from "@app/components/questkit/MediaSliderV2/MediaSliderTypes";
import isEqual from "react-fast-compare";
import { TransloadItAssemblyOptions } from "@app/components/questkit/MediaSliderV2/transloaditClient";
import { SignedFile } from "@questmate/common/dist/item/definitions/FileUploadV2";

const emptyFilesArray: readonly (SignedFile | PersistableFile)[] =
  Object.freeze([]);

const updateFiles = (
  item: ItemRenderData,
  files: PersistableFile[]
): ItemRenderData => {
  return {
    ...item,
    data: {
      ...item.data,
      files: files,
    },
    version: item.version + 1,
  };
};

// Keeps the underlying Transloadit/network cause in the error Sentry receives.
const describeUploadError = ({ error }: FileUpload.Error["errorData"]) => {
  const details = error.details as
    | { error?: string; message?: string }
    | undefined;
  return [`${error.name}: ${error.message}`, details?.error, details?.message]
    .filter(Boolean)
    .join(" | ");
};

export const FileUploadV2Item: React.FC<ItemBaseProps> = (props) => {
  const { item, onItemChange, onItemValidate } = props;

  const onFilesChange = useCallback(
    (files: PersistableFile[]) => {
      onItemChange?.(updateFiles(item, files));
      onItemValidate?.();
    },
    [item, onItemChange, onItemValidate]
  );
  const context = useQuestViewContext();

  let getAssemblyOptions:
    | undefined
    | (() => Promise<TransloadItAssemblyOptions>);
  let files: readonly (SignedFile | PersistableFile)[] = emptyFilesArray;
  if (context.viewContext === "RUN") {
    files = props.item.data?.files ?? emptyFilesArray;
    getAssemblyOptions = async (): Promise<TransloadItAssemblyOptions> => {
      const itemId = `${context.questInstanceId}%7C${item.prototype.id}`;
      const { assemblyOptions } =
        await apiRequest<QuestInstances.GetItemInstanceFileUploadConfiguration.ResponseBody>(
          "get",
          `/questInstances/${context.questInstanceId}/item/${itemId}/uploadConfiguration`,
          undefined,
          context.publicQuestSessionToken
        );

      return assemblyOptions;
    };
  }
  const [state] = useState(
    () =>
      new MediaSliderState({
        initialFiles: files as PersistedFileFromServer[],
        getAssemblyOptions,
      })
  );

  const filePromiseResolversRef = useRef<
    Record<string, { resolve: () => void; reject: (err?: unknown) => void }>
  >({});
  useEffect(() => {
    const listener = () => {
      if (context.viewContext === "RUN") {
        const trackedFileIds = Object.keys(filePromiseResolversRef.current);
        const currentFiles = state.files;

        for (const trackedFileId of trackedFileIds) {
          const { resolve, reject } =
            filePromiseResolversRef.current[trackedFileId];

          const currentFile = currentFiles.find(
            ({ id }) => id === trackedFileId
          );
          if (!currentFile) {
            reject(
              new Error("File deleted. Check if valid and retry submission.")
            );
            delete filePromiseResolversRef.current[trackedFileId];
          } else if (currentFile.state === "ERROR") {
            reject(
              new Error(
                `File upload failed. ${describeUploadError(
                  currentFile.errorData
                )}`
              )
            );
            delete filePromiseResolversRef.current[trackedFileId];
          } else if (
            currentFile.state === "SAVED" ||
            currentFile.state === "READY_TO_SAVE"
          ) {
            // When a file is deleted or becomes 'READY_TO_SAVE' or 'SAVED' make sure
            // to resolve its promise so submit is not blocked.
            resolve();
            delete filePromiseResolversRef.current[trackedFileId];
          }
        }

        for (const currentFile of currentFiles) {
          if (
            currentFile.state !== "SAVED" &&
            currentFile.state !== "READY_TO_SAVE"
          ) {
            const existingResolver =
              filePromiseResolversRef.current[currentFile.id];
            if (!existingResolver) {
              // If the file is queued, in progress, or in an error state,
              // prevent the user from submitting until it is resolved.
              context.preSubmitPromiseTracker.trackPromise(
                new Promise<void>((resolve, reject) => {
                  filePromiseResolversRef.current[currentFile.id] = {
                    resolve,
                    reject,
                  };
                }),
                { allowThrow: true }
              );
            }
          }
        }
      }
    };
    state.on("files:update:detailed", listener);
    return () => {
      state.off("files:update:detailed", listener);
    };
  }, [state, context]);

  useEffect(() => {
    state.updateSavedFiles(files);
    lastSavedRef.current = files.map(getPersistableFileFields);
  }, [state, files]);

  const lastSavedRef = useRef<PersistableFile[]>([]);
  useEffect(() => {
    const listener = (files: MinimalFile[]) => {
      const savedOrReadyToSaveFiles = files
        .map((file) => {
          if (file.state === "SAVED" || file.state === "READY_TO_SAVE") {
            return getPersistableFileFields(file);
          }
          return null;
        })
        .filter((f) => f !== null);

      const lastSaved = lastSavedRef.current;
      if (!isEqual(lastSaved, savedOrReadyToSaveFiles)) {
        lastSavedRef.current = savedOrReadyToSaveFiles;
        onFilesChange(savedOrReadyToSaveFiles);
      }
    };
    state.on("files:update", listener);
    return () => {
      state.off("files:update", listener);
    };
  }, [onFilesChange, state]);

  return (
    <ItemContainerWrapper
      {...props}
      blockNode={
        <MediaSliderV2
          readOnly={props.readOnly ?? false}
          editMode={props.editMode}
          state={state}
          accessibilityLabelledBy={`item-name-label-${props.item.prototype.id}`}
        />
      }
    />
  );
};
